import type { FolderTree as FolderTreeType } from '@/hooks/useFolders'
import { previewSampleState, type SyncPreviewSample } from './syncMapping'
import type { FolderItem } from './WorkspacePicker'

export const PREVIEW_SOURCE_GUID_PREFIX = 'preview-source:'
export const PREVIEW_PLACEHOLDER_GUID_PREFIX = 'preview-placeholder:'
export const PREVIEW_EMPTY_GUID_PREFIX = 'preview-empty:'
export const PREVIEW_SAMPLE_FOLDER_GUID_PREFIX = 'preview-sample-folder:'
export const PREVIEW_SAMPLE_FILE_GUID_PREFIX = 'preview-sample-file:'

function makePreviewFolder(
  name: string,
  guid: string,
  folderId: number,
  children: FolderTreeType[] = [],
): FolderTreeType {
  return {
    folder_id: folderId,
    guid,
    name,
    matter_id: 0,
    path: `/${name}`,
    depth: 0,
    is_active: true,
    created_at: '',
    children,
    document_count: 0,
    token_count: 0,
  }
}

function previewPlaceholderFolder(index: number): FolderTreeType {
  return makePreviewFolder(
    'files and subfolders',
    `${PREVIEW_PLACEHOLDER_GUID_PREFIX}${index}`,
    -10000 - index,
  )
}

function previewEmptyFolder(index: number): FolderTreeType {
  return makePreviewFolder(
    'Empty folder',
    `${PREVIEW_EMPTY_GUID_PREFIX}${index}`,
    -11000 - index,
  )
}

function previewMoreFolder(index: number): FolderTreeType {
  return makePreviewFolder(
    '...',
    `${PREVIEW_PLACEHOLDER_GUID_PREFIX}more:${index}`,
    -12000 - index,
  )
}

function previewSampleNode(sample: SyncPreviewSample, sourceIndex: number, childIndex: number): FolderTreeType {
  const isFile = sample.kind === 'file'
  return makePreviewFolder(
    sample.name,
    `${isFile ? PREVIEW_SAMPLE_FILE_GUID_PREFIX : PREVIEW_SAMPLE_FOLDER_GUID_PREFIX}${sourceIndex}:${childIndex}`,
    -30000 - sourceIndex * 10 - childIndex,
  )
}

export function previewNodesForSampleState(
  state: ReturnType<typeof previewSampleState>,
  sourceIndex: number,
): FolderTreeType[] {
  if (state.type === 'placeholder') {
    return [previewPlaceholderFolder(sourceIndex)]
  }
  if (state.type === 'empty') {
    return [previewEmptyFolder(sourceIndex)]
  }
  return [
    ...state.items.map((sample, childIndex) => previewSampleNode(sample, sourceIndex, childIndex)),
    previewMoreFolder(sourceIndex),
  ]
}

export function previewNodesForSamples(
  samples: SyncPreviewSample[] | null | undefined,
  sourceIndex: number,
): FolderTreeType[] {
  return previewNodesForSampleState(previewSampleState(samples), sourceIndex)
}

export function sourcePreviewChildren(item: FolderItem): SyncPreviewSample[] | null | undefined {
  return (item as { preview_children?: SyncPreviewSample[] | null }).preview_children
}

export function previewSourceFolder(name: string, index: number, children: FolderTreeType[]): FolderTreeType {
  return makePreviewFolder(
    name,
    `${PREVIEW_SOURCE_GUID_PREFIX}${index}`,
    -20000 - index,
    children,
  )
}

export function isPreviewPlaceholderFolder(folder: FolderTreeType): boolean {
  return (
    folder.guid.startsWith(PREVIEW_PLACEHOLDER_GUID_PREFIX)
    || folder.guid.startsWith(PREVIEW_EMPTY_GUID_PREFIX)
  )
}

export function isPreviewSampleFile(folder: FolderTreeType): boolean {
  return folder.guid.startsWith(PREVIEW_SAMPLE_FILE_GUID_PREFIX)
}

export function isPreviewSourceFolder(folder: FolderTreeType): boolean {
  return folder.guid.startsWith(PREVIEW_SOURCE_GUID_PREFIX)
}
